import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from '../api/client';
import { API } from '../api/config';
import {
  Plus,
  FileText,
  History,
  Upload,
  Download,
  X,
  CheckCircle,
  AlertCircle,
  Clock,
  CreditCard,
  Loader2,
} from 'lucide-react';
import { CardNumberElement, CardExpiryElement, CardCvcElement, Elements, useStripe, useElements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { stripeElementStyle } from '../stripeElementStyle';
import {
  emptyExportRefundClaimForm,
  emptyExportRefundProfileForm,
  ExportRefundClaimDetailResponse,
  ExportRefundClaimForm,
  ExportRefundClaimResponse,
  ExportRefundDocumentResponse,
  ExportRefundProfileForm,
  profileResponseToForm,
} from '../exportRefund';

const stripePk = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || 'pk_test_TYooMQauvdEDq54NiTphI7jx';
const stripePromise = loadStripe(stripePk);

const nextComplianceDeadline = (): string => {
  const now = new Date();
  const deadline = new Date(now.getFullYear(), now.getMonth() + 1, 10);
  return deadline.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
};

// ── Partner Visa Card top-up form (BDD Scenario 4-A "轉帳儲值": real Stripe
// payment, not a balance increment — rendered inside an <Elements> wrapper) ──
const CardDepositForm: React.FC<{ userId: string; amount: number; onDeposited: (profileData: any) => void; onCancel: () => void }> = ({ userId, amount, onDeposited, onCancel }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);
  const [cardComplete, setCardComplete] = useState(false);
  const [cardError, setCardError] = useState<string | null>(null);

  const handlePay = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setProcessing(true);
    setCardError(null);
    const token = localStorage.getItem('access_token');

    try {
      const intentRes = await axios.post(API.cardDepositIntent(userId, amount), {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const { client_secret } = intentRes.data;

      const cardNumber = elements.getElement(CardNumberElement);
      if (!cardNumber) { setCardError('Card element not found. Please refresh and try again.'); setProcessing(false); return; }

      const { error: confirmError, paymentIntent } = await stripe.confirmCardPayment(client_secret, {
        payment_method: { card: cardNumber },
      });

      if (confirmError) {
        setCardError(confirmError.message || 'Payment failed. Please check your card details.');
        setProcessing(false);
        return;
      }

      if (paymentIntent?.status !== 'succeeded') {
        setCardError('Unexpected payment status: ' + paymentIntent?.status + '. Please contact support.');
        setProcessing(false);
        return;
      }

      const depositRes = await axios.post(API.depositCard(userId), {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      onDeposited(depositRes.data);
    } catch (err: any) {
      setCardError(err.response?.data?.detail || err.message || 'Payment error. Please try again.');
      setProcessing(false);
    }
  };

  return (
    <form onSubmit={handlePay}>
      <div className="form-group">
        <label className="form-label">Card Number</label>
        <div style={{ padding: '12px', background: '#fff', border: '1px solid var(--sidebar-border)', borderRadius: '6px', minHeight: '44px', display: 'flex', alignItems: 'center' }}>
          <CardNumberElement
            options={{ style: stripeElementStyle }}
            onChange={(e) => { setCardComplete(e.complete); setCardError(e.error ? e.error.message : null); }}
          />
        </div>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '20px' }}>
        <div>
          <label className="form-label">Expiry Date</label>
          <div style={{ padding: '12px', background: '#fff', border: '1px solid var(--sidebar-border)', borderRadius: '6px', minHeight: '44px', display: 'flex', alignItems: 'center' }}>
            <CardExpiryElement options={{ style: stripeElementStyle }} />
          </div>
        </div>
        <div>
          <label className="form-label">CVC</label>
          <div style={{ padding: '12px', background: '#fff', border: '1px solid var(--sidebar-border)', borderRadius: '6px', minHeight: '44px', display: 'flex', alignItems: 'center' }}>
            <CardCvcElement options={{ style: stripeElementStyle }} />
          </div>
        </div>
      </div>

      {cardError && (
        <div style={{ color: '#991B1B', fontSize: '13px', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px', background: '#FEF2F2', padding: '10px', borderRadius: '6px' }}>
          <AlertCircle size={14} /> {cardError}
        </div>
      )}

      <div style={{ display: 'flex', gap: '12px' }}>
        <button type="button" className="btn btn-outline" style={{ flex: 1 }} onClick={onCancel} disabled={processing}>
          Cancel
        </button>
        <button type="submit" className="btn btn-primary" style={{ flex: 1 }} disabled={!stripe || !cardComplete || processing}>
          {processing ? <><Loader2 className="animate-spin" size={16} /> Processing...</> : `Deposit £${amount.toFixed(2)}`}
        </button>
      </div>
    </form>
  );
};

// ── Partner claim shipping-fee payment (BDD Scenario 4-A "我要出貨": pay the
// international shipping fee for a self-purchased claim once documents are
// uploaded — backend endpoints landed in todo.txt 13.4, this closes the
// frontend-wiring gap noted in BDD.txt inline and todo.txt section 6) ──────
type ClaimShippingPaymentMethod = 'stripe' | 'bank_transfer' | 'reward';

const ClaimShippingCardForm: React.FC<{
  userId: string;
  claimId: string;
  amountDue: number;
  onPaid: () => void;
}> = ({ userId, claimId, amountDue, onPaid }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);
  const [cardComplete, setCardComplete] = useState(false);
  const [cardError, setCardError] = useState<string | null>(null);

  const handlePay = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setProcessing(true);
    setCardError(null);
    const token = localStorage.getItem('access_token');
    const headers = { Authorization: `Bearer ${token}` };

    try {
      const intentRes = await axios.post(API.claimShippingPaymentIntent(userId, claimId), {}, { headers });
      const { client_secret } = intentRes.data;

      const cardNumber = elements.getElement(CardNumberElement);
      if (!cardNumber) { setCardError('Card element not found. Please refresh and try again.'); setProcessing(false); return; }

      const { error: confirmError, paymentIntent } = await stripe.confirmCardPayment(client_secret, {
        payment_method: { card: cardNumber },
      });

      if (confirmError) {
        setCardError(confirmError.message || 'Payment failed. Please check your card details.');
        setProcessing(false);
        return;
      }
      if (paymentIntent?.status !== 'succeeded') {
        setCardError('Unexpected payment status: ' + paymentIntent?.status + '. Please contact support.');
        setProcessing(false);
        return;
      }

      await axios.post(API.payClaimShippingFee(userId, claimId), {}, { headers });
      onPaid();
    } catch (err: any) {
      setCardError(err.response?.data?.detail || err.message || 'Payment error. Please try again.');
      setProcessing(false);
    }
  };

  return (
    <form onSubmit={handlePay}>
      <div className="form-group">
        <label className="form-label">Card Number</label>
        <div style={{ padding: '12px', background: '#fff', border: '1px solid var(--sidebar-border)', borderRadius: '6px', minHeight: '44px', display: 'flex', alignItems: 'center' }}>
          <CardNumberElement options={{ style: stripeElementStyle }} onChange={(e) => { setCardComplete(e.complete); setCardError(e.error ? e.error.message : null); }} />
        </div>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '20px' }}>
        <div>
          <label className="form-label">Expiry Date</label>
          <div style={{ padding: '12px', background: '#fff', border: '1px solid var(--sidebar-border)', borderRadius: '6px', minHeight: '44px', display: 'flex', alignItems: 'center' }}>
            <CardExpiryElement options={{ style: stripeElementStyle }} />
          </div>
        </div>
        <div>
          <label className="form-label">CVC</label>
          <div style={{ padding: '12px', background: '#fff', border: '1px solid var(--sidebar-border)', borderRadius: '6px', minHeight: '44px', display: 'flex', alignItems: 'center' }}>
            <CardCvcElement options={{ style: stripeElementStyle }} />
          </div>
        </div>
      </div>
      {cardError && (
        <div style={{ color: '#991B1B', fontSize: '13px', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px', background: '#FEF2F2', padding: '10px', borderRadius: '6px' }}>
          <AlertCircle size={14} /> {cardError}
        </div>
      )}
      <button type="submit" className="btn btn-primary" style={{ width: '100%' }} disabled={!stripe || !cardComplete || processing}>
        {processing ? <><Loader2 className="animate-spin" size={16} /> Processing...</> : `Pay £${amountDue.toFixed(2)}`}
      </button>
    </form>
  );
};

const ClaimShippingModal: React.FC<{ userId: string; claimId: string; onClose: () => void; onDone: () => void }> = ({ userId, claimId, onClose, onDone }) => {
  const [baseShippingFee, setBaseShippingFee] = useState('');
  const [weightKg, setWeightKg] = useState('');
  const [volumeM3, setVolumeM3] = useState('');
  const [value, setValue] = useState('');
  const [contents, setContents] = useState('');
  const [recipientName, setRecipientName] = useState('');
  const [recipientPhone, setRecipientPhone] = useState('');
  const [insured, setInsured] = useState(false);
  // BDD 4-A line 168: 「於 UI 優先引導客戶使用『Reward 返點全額抵扣』或『英國國內銀行
  // 轉帳』」 — reward is the UI-prioritized default, not card.
  const [paymentMethod, setPaymentMethod] = useState<ClaimShippingPaymentMethod>('reward');
  const [quote, setQuote] = useState<{ amount_due: number } | null>(null);
  const [rewardBalance, setRewardBalance] = useState<number | null>(null);
  const [quoting, setQuoting] = useState(false);
  const [paying, setPaying] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState<ClaimShippingPaymentMethod | null>(null);

  const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('access_token')}` });

  useEffect(() => {
    axios.get(API.getRewardBalance(), { headers: authHeaders() })
      .then((res) => setRewardBalance(res.data.balance))
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getQuote = async () => {
    if (!baseShippingFee || Number(baseShippingFee) <= 0) return;
    setQuoting(true);
    setError('');
    try {
      const res = await axios.post(
        API.claimShippingQuote(userId, claimId),
        {
          base_shipping_fee: Number(baseShippingFee),
          payment_method: paymentMethod,
          package_weight_kg: weightKg ? Number(weightKg) : undefined,
          package_volume_m3: volumeM3 ? Number(volumeM3) : undefined,
          package_value: value ? Number(value) : undefined,
          package_contents: contents.trim() || undefined,
          recipient_info: (recipientName.trim() || recipientPhone.trim())
            ? { name: recipientName.trim(), phone: recipientPhone.trim() }
            : undefined,
          insured,
        },
        { headers: authHeaders() },
      );
      setQuote(res.data);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to get a shipping quote.');
    } finally {
      setQuoting(false);
    }
  };

  const payWithReward = async () => {
    setPaying(true);
    setError('');
    try {
      await axios.post(API.claimShippingPaymentIntent(userId, claimId), {}, { headers: authHeaders() });
      await axios.post(API.payClaimShippingFee(userId, claimId), {}, { headers: authHeaders() });
      setDone('reward');
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to pay with reward balance.');
    } finally {
      setPaying(false);
    }
  };

  const startBankTransfer = async () => {
    setPaying(true);
    setError('');
    try {
      await axios.post(API.claimShippingPaymentIntent(userId, claimId), {}, { headers: authHeaders() });
      setDone('bank_transfer');
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to start bank transfer.');
    } finally {
      setPaying(false);
    }
  };

  const insufficientReward = rewardBalance !== null && quote !== null && rewardBalance < quote.amount_due;

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px', zIndex: 1000 }}>
      <div className="section-card" style={{ width: '100%', maxWidth: '460px', padding: '28px' }}>
        <h2 className="card-title" style={{ marginBottom: '8px' }}>Request Shipping</h2>

        {done ? (
          <div>
            <p style={{ color: '#166534', background: '#F0FDF4', border: '1px solid #BBF7D0', borderRadius: '6px', padding: '12px 16px', fontSize: '14px', marginBottom: '20px' }}>
              {done === 'bank_transfer'
                ? "We've recorded your shipping fee. Our team will confirm it once your bank transfer arrives."
                : 'Shipping fee paid. Our logistics team will produce your Commercial Invoice and label next.'}
            </p>
            <button className="btn btn-primary" style={{ width: '100%' }} onClick={onDone}>Done</button>
          </div>
        ) : !quote ? (
          <>
            <p style={{ color: '#64748B', fontSize: '13px', marginBottom: '20px' }}>
              Tell us about your package and the base shipping fee quoted by our logistics partner. Stripe
              card payment adds a 5% surcharge; bank transfer and reward balance have no markup.
            </p>
            <div className="form-group">
              <label className="form-label" htmlFor="claim-shipping-base-fee-input">Base Shipping Fee (£)</label>
              <input id="claim-shipping-base-fee-input" className="form-input" type="number" step="0.01" value={baseShippingFee} onChange={(e) => setBaseShippingFee(e.target.value)} placeholder="0.00" />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              <div className="form-group">
                <label className="form-label" htmlFor="claim-shipping-weight-input">Weight (kg)</label>
                <input id="claim-shipping-weight-input" className="form-input" type="number" step="0.1" value={weightKg} onChange={(e) => setWeightKg(e.target.value)} />
              </div>
              <div className="form-group">
                <label className="form-label" htmlFor="claim-shipping-volume-input">Volume (m³)</label>
                <input id="claim-shipping-volume-input" className="form-input" type="number" step="0.01" value={volumeM3} onChange={(e) => setVolumeM3(e.target.value)} />
              </div>
            </div>
            <div className="form-group">
              <label className="form-label" htmlFor="claim-shipping-value-input">Declared Value (£)</label>
              <input id="claim-shipping-value-input" className="form-input" type="number" step="0.01" value={value} onChange={(e) => setValue(e.target.value)} />
            </div>
            <div className="form-group">
              <label className="form-label" htmlFor="claim-shipping-contents-input">Package Contents</label>
              <textarea
                id="claim-shipping-contents-input"
                className="form-input"
                style={{ minHeight: '60px' }}
                value={contents}
                onChange={(e) => setContents(e.target.value)}
                placeholder="e.g. 1x cashmere scarf, 1x leather wallet"
              />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              <div className="form-group">
                <label className="form-label" htmlFor="claim-shipping-recipient-name-input">Recipient Name</label>
                <input id="claim-shipping-recipient-name-input" className="form-input" value={recipientName} onChange={(e) => setRecipientName(e.target.value)} />
              </div>
              <div className="form-group">
                <label className="form-label" htmlFor="claim-shipping-recipient-phone-input">Recipient Phone</label>
                <input id="claim-shipping-recipient-phone-input" className="form-input" value={recipientPhone} onChange={(e) => setRecipientPhone(e.target.value)} />
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
              <input type="checkbox" id="claim-shipping-insured" checked={insured} onChange={(e) => setInsured(e.target.checked)} />
              <label htmlFor="claim-shipping-insured" style={{ fontSize: '13px', cursor: 'pointer' }}>Add shipping insurance</label>
            </div>
            <div className="form-group">
              <label className="form-label">Payment Method</label>
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                {([
                  { value: 'reward' as const, label: 'Reward Balance' },
                  { value: 'bank_transfer' as const, label: 'Bank Transfer' },
                  { value: 'stripe' as const, label: 'Card' },
                ]).map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    className={paymentMethod === opt.value ? 'btn btn-primary' : 'btn btn-outline'}
                    style={{ flex: '1 1 120px', fontSize: '13px' }}
                    onClick={() => setPaymentMethod(opt.value)}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
            {error && (
              <div style={{ color: '#991B1B', fontSize: '13px', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px', background: '#FEF2F2', padding: '10px', borderRadius: '6px' }}>
                <AlertCircle size={14} /> {error}
              </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '16px' }}>
              <button className="btn btn-outline" onClick={onClose} disabled={quoting}>Cancel</button>
              <button className="btn btn-primary" onClick={getQuote} disabled={quoting || !baseShippingFee || Number(baseShippingFee) <= 0}>
                {quoting ? 'Getting Quote...' : 'Get Quote'}
              </button>
            </div>
          </>
        ) : (
          <>
            <div style={{ background: 'var(--text-charcoal)', color: '#fff', padding: '20px', borderRadius: '8px', marginBottom: '20px' }}>
              <p style={{ fontSize: '13px', opacity: 0.7, marginBottom: '6px' }}>Amount Due ({paymentMethod === 'stripe' ? 'card' : paymentMethod.replace('_', ' ')})</p>
              <p style={{ fontSize: '28px', fontWeight: 700, color: '#D4AF37' }}>£{quote.amount_due.toFixed(2)}</p>
            </div>

            {error && (
              <div style={{ color: '#991B1B', fontSize: '13px', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px', background: '#FEF2F2', padding: '10px', borderRadius: '6px' }}>
                <AlertCircle size={14} /> {error}
              </div>
            )}

            {paymentMethod === 'stripe' && (
              <ClaimShippingCardForm userId={userId} claimId={claimId} amountDue={quote.amount_due} onPaid={() => setDone('stripe')} />
            )}
            {paymentMethod === 'reward' && (
              <>
                <p style={{ fontSize: '13px', color: '#64748B', marginBottom: '12px' }}>
                  Reward balance: £{(rewardBalance ?? 0).toFixed(2)}
                </p>
                {insufficientReward && (
                  <div style={{ color: '#991B1B', fontSize: '13px', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px', background: '#FEF2F2', padding: '10px', borderRadius: '6px' }}>
                    <AlertCircle size={14} /> Your reward balance doesn't cover this fee.
                  </div>
                )}
                <button className="btn btn-primary" style={{ width: '100%' }} onClick={payWithReward} disabled={paying || insufficientReward}>
                  {paying ? 'Processing...' : `Pay £${quote.amount_due.toFixed(2)} with Reward Balance`}
                </button>
              </>
            )}
            {paymentMethod === 'bank_transfer' && (
              <>
                <p style={{ fontSize: '13px', color: '#64748B', marginBottom: '12px' }}>
                  We'll send you our bank details. Your shipment is held until our team confirms your transfer has arrived.
                </p>
                <button className="btn btn-primary" style={{ width: '100%' }} onClick={startBankTransfer} disabled={paying}>
                  {paying ? 'Processing...' : 'Confirm Bank Transfer Payment'}
                </button>
              </>
            )}

            <div style={{ marginTop: '16px' }}>
              <button className="btn btn-outline" style={{ width: '100%' }} onClick={() => setQuote(null)} disabled={paying}>
                Back
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

interface PendingClaimFiles {
  invoice: File[];
  export_form: File[];
}

const RefundClaims: React.FC = () => {
  const { user } = useAuth();
  const location = useLocation();
  const [claims, setClaims] = useState<ExportRefundClaimDetailResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [showNewClaim, setShowNewClaim] = useState(false);
  const [claimForm, setClaimForm] = useState<ExportRefundClaimForm>(emptyExportRefundClaimForm);
  const [pendingFiles, setPendingFiles] = useState<PendingClaimFiles>({
    invoice: [],
    export_form: [],
  });
  const [cardProfile, setCardProfile] = useState<ExportRefundProfileForm>(emptyExportRefundProfileForm);
  const [depositAmount, setDepositAmount] = useState('');
  const [showDepositModal, setShowDepositModal] = useState(false);
  const [complianceDocType, setComplianceDocType] = useState<Record<string, string>>({});
  const [checkingCompliance, setCheckingCompliance] = useState<string | null>(null);
  const [shippingClaimId, setShippingClaimId] = useState<string | null>(null);

  useEffect(() => {
    if (location.state) {
      const { transactionId, amount, shopName, date } = location.state as any;
      setClaimForm(prev => ({
        ...prev,
        external_transaction_id: transactionId || '',
        total_amount: amount?.toString() || '',
        shop_name: shopName || '',
        transaction_date: date || new Date().toISOString().split('T')[0],
      }));
      setShowNewClaim(true);
    }
  }, [location.state]);

  const loadClaims = useCallback(async () => {
    if (!user) return;
    const token = localStorage.getItem('access_token');
    const headers = { Authorization: `Bearer ${token}` };
    try {
      const response = await axios.get(API.listExportRefundClaims(user.id), { headers });
      const basicClaims = response.data as ExportRefundClaimResponse[];
      const detailedClaims = await Promise.all(
        basicClaims.map(async (claim) => {
          const detail = await axios.get(API.getExportRefundClaimDetail(user.id, claim.id), { headers });
          return detail.data as ExportRefundClaimDetailResponse;
        }),
      );
      setClaims(detailedClaims);
    } catch (err) {
      console.error('Failed to load claims:', err);
      setError('Failed to load refund claims.');
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    loadClaims();
  }, [loadClaims]);

  const loadCardProfile = useCallback(async () => {
    if (!user) return;
    const token = localStorage.getItem('access_token');
    try {
      const response = await axios.get(API.getExportRefundProfile(user.id), {
        headers: { Authorization: `Bearer ${token}` },
      });
      setCardProfile(profileResponseToForm(response.data));
    } catch (err) {
      console.error('Failed to load card profile:', err);
    }
  }, [user]);

  useEffect(() => {
    loadCardProfile();
  }, [loadCardProfile]);

  const isPartnerCardHolder = cardProfile.company_card_requested;

  const handleDeposited = (profileData: any) => {
    setCardProfile(profileResponseToForm(profileData));
    setDepositAmount('');
    setShowDepositModal(false);
    setMessage('Funds deposited to your Partner Visa Card.');
  };

  const checkCompliance = async (claim: ExportRefundClaimDetailResponse) => {
    if (!user) return;
    const documentType = complianceDocType[claim.id] || (claim.amounts.total_amount && claim.amounts.total_amount >= 250 ? 'company_invoice' : 'receipt');
    setCheckingCompliance(claim.id);
    setError('');
    setMessage('');
    const token = localStorage.getItem('access_token');
    try {
      await axios.post(
        API.checkClaimCompliance(user.id, claim.id),
        {
          document_type: documentType,
          purchase_date: claim.purchase.transaction_date || undefined,
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setMessage('Compliance check complete.');
      await loadClaims();
      await loadCardProfile();
    } catch (err) {
      console.error('Compliance check failed:', err);
      setError('Compliance check failed. Please verify the document type and try again.');
    } finally {
      setCheckingCompliance(null);
    }
  };

  const setFormField = (field: keyof ExportRefundClaimForm, value: string) => {
    setClaimForm((current) => ({ ...current, [field]: value }));
  };

  const calculatedYouWillGet = useMemo(() => (
    claimForm.total_amount ? Number(claimForm.total_amount) * 0.08 : 0
  ), [claimForm.total_amount]);

  const addFiles = (type: keyof PendingClaimFiles, files: FileList | null) => {
    if (!files) return;
    setPendingFiles((current) => ({
      ...current,
      [type]: [...current[type], ...Array.from(files)],
    }));
  };

  const removeFile = (type: keyof PendingClaimFiles, index: number) => {
    setPendingFiles((current) => ({
      ...current,
      [type]: current[type].filter((_, i) => i !== index),
    }));
  };

  const createClaim = async () => {
    if (!user) return;
    setCreating(true);
    setMessage('');
    setError('');
    const token = localStorage.getItem('access_token');
    const headers = { Authorization: `Bearer ${token}` };

    try {
      const claimResponse = await axios.post(
        API.createExportRefundClaim(user.id),
        {
          ...claimForm,
          total_amount: Number(claimForm.total_amount),
          vat_amount: Number((Number(claimForm.total_amount) * 0.1666).toFixed(2)),
          price_without_vat: Number((Number(claimForm.total_amount) - Number((Number(claimForm.total_amount) * 0.1666).toFixed(2))).toFixed(2)),
          external_transaction_id: claimForm.external_transaction_id || null,
        },
        { headers },
      );

      const claimId = claimResponse.data.id;

      for (const [type, files] of Object.entries(pendingFiles)) {
        for (const file of files) {
          const formData = new FormData();
          formData.append('document_type', type);
          formData.append('file', file);
          await axios.post(API.uploadExportRefundDocument(user.id, claimId), formData, {
            headers: { ...headers, 'Content-Type': 'multipart/form-data' },
          });
        }
      }

      setClaimForm(emptyExportRefundClaimForm);
      setPendingFiles({ invoice: [], export_form: [] });
      setMessage('Refund claim created successfully.');
      setShowNewClaim(false);
      await loadClaims();
    } catch (err) {
      console.error('Failed to create claim:', err);
      setError('Could not create the refund claim.');
    } finally {
      setCreating(false);
    }
  };

  const downloadDocument = async (doc: ExportRefundDocumentResponse) => {
    if (!user) return;
    const token = localStorage.getItem('access_token');
    try {
      const response = await axios.get(API.downloadExportRefundDocument(user.id, doc.id), {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = url;
      link.download = doc.original_filename;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Download failed:', err);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status.toLowerCase()) {
      case 'refunded':
      case 'paid':
      case 'approved':
        return <CheckCircle size={16} className="text-success" />;
      case 'processing':
      case 'under_review':
      case 'submitted':
        return <Clock size={16} className="text-pending" />;
      case 'rejected':
      case 'error':
        return <AlertCircle size={16} className="text-danger" />;
      default:
        return <History size={16} />;
    }
  };

  const getStatusClass = (status: string) => {
    switch (status.toLowerCase()) {
      case 'refunded':
      case 'paid':
      case 'approved':
        return 'status-active';
      case 'processing':
      case 'under_review':
      case 'submitted':
        return 'status-pending';
      case 'rejected':
      case 'error':
        return 'status-cancelled';
      default:
        return 'status-outline';
    }
  };

  if (loading) return <div>Loading claims...</div>;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '32px' }}>
        <h1 className="page-title" style={{ margin: 0 }}>Refund Claims</h1>
        {!showNewClaim && (
          <button className="btn btn-primary" onClick={() => setShowNewClaim(true)}>
            <Plus size={18} /> New Claim
          </button>
        )}
      </div>

      {(message || error) && (
        <div
          style={{
            marginBottom: '24px',
            padding: '16px',
            borderRadius: '8px',
            background: error ? '#FEF2F2' : '#F0FDF4',
            color: error ? '#991B1B' : '#166534',
            border: `1px solid ${error ? '#FCA5A5' : '#BBF7D0'}`,
            fontSize: '14px',
          }}
        >
          {error || message}
        </div>
      )}

      {isPartnerCardHolder && (
        <div className="section-card" style={{ marginBottom: '24px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
            <CreditCard size={20} className="text-primary" />
            <h3 className="card-title" style={{ margin: 0 }}>Partner Visa Card</h3>
            <div className={`status-pill ${cardProfile.card_status === 'active' ? 'status-active' : cardProfile.card_status === 'frozen' || cardProfile.card_status === 'violation' || cardProfile.card_status === 'legal_escalation' ? 'status-cancelled' : 'status-outline'}`}>
              {cardProfile.card_status.replace(/_/g, ' ').toUpperCase()}
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '20px', marginBottom: '20px' }}>
            <div>
              <div className="form-label">Card Balance</div>
              <div style={{ fontWeight: 600, fontSize: '20px' }}>£{cardProfile.card_balance.toFixed(2)}</div>
            </div>
            <div>
              <div className="form-label">Card Number</div>
              <div style={{ fontWeight: 600 }}>{cardProfile.card_number_last4 ? `•••• ${cardProfile.card_number_last4}` : 'Not issued'}</div>
            </div>
            <div>
              <div className="form-label">Next Invoice Deadline (10th)</div>
              <div style={{ fontWeight: 600 }}>{nextComplianceDeadline()}</div>
            </div>
          </div>

          {cardProfile.card_status === 'frozen' || cardProfile.card_status === 'violation' || cardProfile.card_status === 'legal_escalation' ? (
            <div style={{ padding: '12px', borderRadius: '8px', background: '#FEF2F2', color: '#991B1B', border: '1px solid #FCA5A5', fontSize: '14px' }}>
              Card frozen: {cardProfile.risk_freeze_reason || 'Compliance violation detected.'}
            </div>
          ) : cardProfile.card_status === 'pending_issuance' ? (
            <div style={{ padding: '12px', borderRadius: '8px', background: '#FFFBEB', color: '#92400E', border: '1px solid #FDE68A', fontSize: '14px' }}>
              Your card is approved and paid for — our team is issuing it now. You'll be able to deposit funds once it's active.
            </div>
          ) : (
            <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
              <input
                type="number"
                className="form-input"
                style={{ maxWidth: '160px' }}
                placeholder="Amount (£)"
                value={depositAmount}
                onChange={(e) => setDepositAmount(e.target.value)}
              />
              <button
                className="btn btn-outline"
                onClick={() => setShowDepositModal(true)}
                disabled={!depositAmount || Number(depositAmount) <= 0}
              >
                Deposit Funds
              </button>
            </div>
          )}
        </div>
      )}

      {showDepositModal && user && (
        <div
          style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center',
            justifyContent: 'center', zIndex: 1000, padding: '20px',
          }}
        >
          <div className="section-card" style={{ maxWidth: '440px', width: '100%', padding: '32px' }}>
            <h3 className="card-title" style={{ marginBottom: '12px' }}>Deposit to Partner Visa Card</h3>
            <p style={{ color: '#64748B', fontSize: '14px', marginBottom: '24px' }}>
              This top-up is charged to your card via Stripe and added to your Partner Visa Card balance immediately.
            </p>
            <Elements stripe={stripePromise}>
              <CardDepositForm
                userId={user.id}
                amount={Number(depositAmount)}
                onDeposited={handleDeposited}
                onCancel={() => setShowDepositModal(false)}
              />
            </Elements>
          </div>
        </div>
      )}

      {showNewClaim && (
        <div className="section-card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
            <h2 className="card-title" style={{ margin: 0 }}>Create New Refund Claim</h2>
            <button className="btn btn-outline" style={{ padding: '8px' }} onClick={() => setShowNewClaim(false)}>
              <X size={20} />
            </button>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '20px' }}>
            <div className="form-group">
              <label className="form-label">Transaction Date</label>
              <input type="date" className="form-input" value={claimForm.transaction_date} onChange={(e) => setFormField('transaction_date', e.target.value)} />
            </div>
            <div className="form-group">
              <label className="form-label">Shop Name</label>
              <input className="form-input" value={claimForm.shop_name} onChange={(e) => setFormField('shop_name', e.target.value)} placeholder="Harrods" />
            </div>
            <div className="form-group">
              <label className="form-label">Brand Name</label>
              <input className="form-input" value={claimForm.brand_name} onChange={(e) => setFormField('brand_name', e.target.value)} placeholder="Louis Vuitton" />
            </div>
            <div className="form-group">
              <label className="form-label">Spending Amount (£)</label>
              <input type="number" className="form-input" value={claimForm.total_amount} onChange={(e) => setFormField('total_amount', e.target.value)} placeholder="0.00" />
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Item Description</label>
            <textarea
              className="form-input"
              value={claimForm.item_description}
              onChange={(e) => setFormField('item_description', e.target.value)}
              placeholder="Describe the items purchased..."
              style={{ minHeight: '80px', resize: 'vertical' }}
            />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '32px' }}>
            <div className="upload-card" style={{ padding: '20px', textAlign: 'left' }}>
              <div style={{ fontWeight: 600, marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Upload size={18} /> Invoices / Receipts
              </div>
              <input type="file" multiple id="invoice-upload" style={{ display: 'none' }} onChange={(e) => addFiles('invoice', e.target.files)} />
              <button className="btn btn-outline" style={{ width: '100%' }} onClick={() => document.getElementById('invoice-upload')?.click()}>
                Select Files
              </button>
              <div style={{ marginTop: '12px', display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                {pendingFiles.invoice.map((file, index) => (
                  <div key={index} style={{ background: '#F1F5F9', padding: '4px 8px', borderRadius: '4px', fontSize: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    {file.name}
                    <X size={14} style={{ cursor: 'pointer', color: '#EF4444' }} onClick={() => removeFile('invoice', index)} />
                  </div>
                ))}
              </div>
            </div>

            <div className="upload-card" style={{ padding: '20px', textAlign: 'left' }}>
              <div style={{ fontWeight: 600, marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Upload size={18} /> Export Forms
              </div>
              <input type="file" multiple id="export-upload" style={{ display: 'none' }} onChange={(e) => addFiles('export_form', e.target.files)} />
              <button className="btn btn-outline" style={{ width: '100%' }} onClick={() => document.getElementById('export-upload')?.click()}>
                Select Files
              </button>
              <div style={{ marginTop: '12px', display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                {pendingFiles.export_form.map((file, index) => (
                  <div key={index} style={{ background: '#F1F5F9', padding: '4px 8px', borderRadius: '4px', fontSize: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    {file.name}
                    <X size={14} style={{ cursor: 'pointer', color: '#EF4444' }} onClick={() => removeFile('export_form', index)} />
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', borderTop: '1px solid var(--sidebar-border)', paddingTop: '24px' }}>
            <div style={{ flex: 1, display: 'flex', alignItems: 'center', fontSize: '14px', color: '#64748B' }}>
              Estimated Refund: <strong style={{ marginLeft: '8px', color: '#0F172A' }}>£{calculatedYouWillGet.toFixed(2)}</strong>
            </div>
            <button className="btn btn-outline" onClick={() => setShowNewClaim(false)}>Cancel</button>
            <button className="btn btn-primary" onClick={createClaim} disabled={creating || !claimForm.total_amount}>
              {creating ? 'Creating...' : 'Submit Claim'}
            </button>
          </div>
        </div>
      )}

      <div style={{ display: 'grid', gap: '16px' }}>
        {claims.length > 0 ? (
          claims.map((claim) => (
            <div key={claim.id} className="section-card" style={{ padding: '24px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px' }}>
                <div style={{ display: 'flex', gap: '20px', alignItems: 'center' }}>
                  <div style={{ padding: '12px', background: '#F8FAFC', borderRadius: '10px', color: '#64748B' }}>
                    <FileText size={24} />
                  </div>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: '18px' }}>{claim.reference_number}</div>
                    <div style={{ fontSize: '14px', color: '#64748B', marginTop: '4px' }}>
                      {claim.purchase.shop_name} {claim.purchase.brand_name ? `• ${claim.purchase.brand_name}` : ''} • {claim.purchase.transaction_date}
                    </div>
                  </div>
                </div>
                <div className={`status-pill ${getStatusClass(claim.status.progress_status)}`} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  {getStatusIcon(claim.status.progress_status)}
                  {claim.status.progress_status.toUpperCase().replace('_', ' ')}
                </div>
              </div>

              {claim.status.progress_status === 'error' && claim.status.status_reason && (
                <div style={{ marginBottom: '20px', padding: '12px', borderRadius: '8px', background: '#FEF2F2', color: '#991B1B', border: '1px solid #FCA5A5', fontSize: '14px' }}>
                  <strong>Error:</strong> {claim.status.status_reason}
                </div>
              )}

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '24px', borderTop: '1px solid var(--sidebar-border)', paddingTop: '20px' }}>
                <div>
                  <div className="form-label">Spending Amount</div>
                  <div style={{ fontWeight: 600 }}>£{claim.amounts.total_amount?.toFixed(2)}</div>
                </div>
                <div>
                  <div className="form-label">Estimated Refund</div>
                  {/* BDD 4-A line 289: 「消費金額 x 0.08」 — cashback is credited
                      off total_amount (export_refund/service.py::
                      _credit_claim_cashback), not price_without_vat (the
                      VAT-exclusive figure, ~16.7% lower) — this used to
                      understate the actual credited amount. Found during a
                      sixth frontend-coverage audit. */}
                  <div style={{ fontWeight: 600, color: '#10B981' }}>£{((claim.amounts.total_amount || 0) * 0.08).toFixed(2)}</div>
                </div>
                <div>
                  <div className="form-label">Payment Method</div>
                  <div style={{ fontWeight: 600, textTransform: 'capitalize' }}>{claim.payment_method || 'Card'}</div>
                </div>
              </div>

              {claim.documents && claim.documents.length > 0 && (
                <div style={{ marginTop: '20px', display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                  {claim.documents.map((doc) => (
                    <button
                      key={doc.id}
                      onClick={() => downloadDocument(doc)}
                      className="btn btn-outline"
                      style={{ padding: '6px 12px', fontSize: '12px', minHeight: 'auto' }}
                    >
                      <Download size={14} /> {(
                        {
                          invoice: 'Invoice',
                          export_form: 'Export Form',
                          logistics_label: 'Logistics Label',
                          commercial_invoice: 'Commercial Invoice',
                        } as Record<string, string>
                      )[doc.document_type] || doc.document_type}
                    </button>
                  ))}
                </div>
              )}

              {isPartnerCardHolder && (
                <div style={{ marginTop: '20px', borderTop: '1px solid var(--sidebar-border)', paddingTop: '20px' }}>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', marginBottom: '12px' }}>
                    {claim.status.compliance_violation && (
                      <div className="status-pill status-cancelled">VIOLATION: £{claim.status.penalty_fee_amount.toFixed(2)} PENALTY</div>
                    )}
                    {claim.status.late_penalty_applied && !claim.status.compliance_violation && (
                      <div className="status-pill status-pending">LATE: £{claim.status.penalty_fee_amount.toFixed(2)} FEE</div>
                    )}
                    {claim.status.settlement_available_at && !claim.status.reward_credited && !claim.status.compliance_violation && (
                      <div className="status-pill status-pending" title="BDD 4-B: a late claim's cashback settlement is deferred to the next settlement cycle">
                        CASHBACK AVAILABLE FROM {new Date(claim.status.settlement_available_at).toLocaleDateString()}
                      </div>
                    )}
                    {!claim.status.late_penalty_applied && !claim.status.compliance_violation && claim.status.document_type && (
                      <div className="status-pill status-active">ON TIME</div>
                    )}
                    {claim.status.reward_credited && (
                      <div className="status-pill status-active">CASHBACK CREDITED: £{(claim.status.reward_credited_amount ?? 0).toFixed(2)}</div>
                    )}
                    {claim.status.shipping_fee_paid && (
                      <div className="status-pill status-active">SHIPPING PAID: £{(claim.status.shipping_estimated_fee ?? 0).toFixed(2)}</div>
                    )}
                    {!claim.status.shipping_fee_paid && claim.status.shipping_payment_method === 'bank_transfer' && (
                      <div className="status-pill status-pending">SHIPPING: AWAITING TRANSFER (£{(claim.status.shipping_estimated_fee ?? 0).toFixed(2)})</div>
                    )}
                  </div>
                  <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
                    <select
                      className="form-input"
                      style={{ maxWidth: '220px' }}
                      value={complianceDocType[claim.id] || (claim.amounts.total_amount && claim.amounts.total_amount >= 250 ? 'company_invoice' : 'receipt')}
                      onChange={(e) => setComplianceDocType((current) => ({ ...current, [claim.id]: e.target.value }))}
                    >
                      <option value="company_invoice">Company Invoice (£250+)</option>
                      <option value="receipt">Purchase Receipt (under £250)</option>
                    </select>
                    <button
                      className="btn btn-outline"
                      onClick={() => checkCompliance(claim)}
                      disabled={checkingCompliance === claim.id || !claim.documents?.length}
                    >
                      {checkingCompliance === claim.id ? 'Checking...' : 'Check Compliance'}
                    </button>
                    {!claim.status.shipping_fee_paid && !claim.status.shipping_payment_method && (
                      <button className="btn btn-outline" onClick={() => setShippingClaimId(claim.id)}>
                        Request Shipping
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))
        ) : (
          <div className="section-card" style={{ textAlign: 'center', padding: '80px', color: '#64748B' }}>
            <FileText size={64} style={{ opacity: 0.1, marginBottom: '24px' }} />
            <h2 style={{ color: '#0F172A', marginBottom: '8px' }}>No Refund Claims</h2>
            <p>You haven't submitted any refund claims yet.</p>
            <button className="btn btn-primary" style={{ marginTop: '32px' }} onClick={() => setShowNewClaim(true)}>
              <Plus size={18} /> Create Your First Claim
            </button>
          </div>
        )}
      </div>

      {shippingClaimId && user && (
        <Elements stripe={stripePromise}>
          <ClaimShippingModal
            userId={user.id}
            claimId={shippingClaimId}
            onClose={() => setShippingClaimId(null)}
            onDone={() => {
              setShippingClaimId(null);
              loadClaims();
            }}
          />
        </Elements>
      )}
    </div>
  );
};

export default RefundClaims;
